import React, { useState } from "react";
import { Flex, Input, Typography, Dropdown, Avatar, Tag, Breadcrumb, Button, Tooltip } from "antd";
import {
  Search01Icon,
  ArrowDown01Icon,
  Logout01Icon,
  Settings02Icon,
  BankIcon,
  UserIcon,
  File01Icon,
  Calendar03Icon,
  Coffee01Icon,
  UserGroupIcon,
  Clock01Icon,
  Camera01Icon,
} from "hugeicons-react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../../redux/slices/authSlice";
import { clearSession } from "../../../redux/slices/sessionSlice";
import { removeCookie, getCookie } from "../../../utils/cookieUtils";
import useGlobalSearchManager from "../../../hooks/useGlobalSearchManager";
import GlobalSearchDropdown from "./GlobalSearchDropdown";
import TopNavbarNotifications from "./TopNavbarNotifications";
import LogoutConfirmModal from "../logout-modal/LogoutConfirmModal";
import ProfilePhotoModal from "../profile-photo-modal/ProfilePhotoModal";
import useAutoBreakTracker from "../../../hooks/useAutoBreakTracker";
import "./topNavbar.css";

const { Text, Title } = Typography;

const isMac = typeof window !== "undefined" && /(Mac|iPhone|iPod|iPad)/i.test(navigator?.userAgentData?.platform || navigator?.platform || navigator?.userAgent || "");
const SEARCH_SHORTCUT_LABEL = isMac ? "⌘K" : "Ctrl+K";

function formatBreakTime(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * TopNavbar Component
 *
 * Shared Core Shell + Role-Driven Navigation:
 * - Common Shell: Breadcrumbs, dynamic section title, global search bar with live multi-entity suggestions.
 * - Superadmin Role (super_admin):
 *     • Root indicator & Enterprise requests / tenant shortcuts
 * - Admin Role (company_admin / admin):
 *     • Company status & code
 *     • Dropdown with Company Settings, App Settings, Invoice & Proposal management
 *     • Sign Out action
 * - Employee Role (employee / sales_rep):
 *     • "Today Follow-ups" quick button
 *     • "Break" status / timer toggle button
 *     • Dropdown with Sales Rep profile & My Leads shortcuts
 *     • Sign Out action
 */
function TopNavbar({ title = "Overview", activeTab = "overview", onNavigateTab, onLogout, onSearch, onSelectRecord, todayFollowupsCount = 0 }) {
  const dispatch = useDispatch();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  // Redux state extraction
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const companyInfo = useSelector((state) => state.executor?.companyInfo);
  const role = user?.role || "company_admin";
  const isSuperAdmin = role === "super_admin";
  const isEmployee = !isSuperAdmin && (role === "employee" || role === "sales_rep");

  const companyName = isSuperAdmin ? "DealVoice System" : companyInfo?.companyName || user?.companyName || "Softrate Technologies";
  const companyCode = isSuperAdmin ? "SUPERADMIN" : companyInfo?.companyCode || user?.companyCode || "";

  let displayName = companyName;
  if (isSuperAdmin) {
    displayName = "Super Admin";
  } else if (isEmployee) {
    displayName = user?.name || "Employee";
  }

  // Automatic Inactivity Break Tracker for Employees
  const autoBreak = useAutoBreakTracker({
    isEmployee,
    employeeId: isEmployee ? (user?.id || user?._id || user?.employeeId || getCookie("userId")) : null,
    employeeName: displayName,
    companyCode,
  });

  // Global Search Manager with Debounce and AbortController
  const searchManager = useGlobalSearchManager({
    activeTab,
    onSelectRecord: (item) => {
      if (onSelectRecord) {
        onSelectRecord(item);
      } else if (onNavigateTab) {
        onNavigateTab(item.targetTab, item);
      }
      if (onSearch) {
        onSearch(item.title);
      }
    },
    onFilterTab: (targetTab, term, recordData) => {
      if (targetTab && onNavigateTab) {
        onNavigateTab(targetTab, recordData);
      }
      if (onSearch) {
        onSearch(term);
      }
    },
  });

  const getInitials = (name) => {
    if (isSuperAdmin) return "SA";
    if (!name) return isEmployee ? "EM" : "SL";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleConfirmLogout = () => {
    setIsLogoutModalOpen(false);
    dispatch(logout());
    dispatch(clearSession());
    removeCookie("token");
    removeCookie("role");
    removeCookie("companyCode");
    if (onLogout) onLogout();
  };

  const navBtn = (tab, label, color = "#64748b", fontWeight = 400) => (
    <button
      type="button"
      onClick={() => onNavigateTab?.(tab)}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color,
        fontWeight,
        fontFamily: "inherit",
        fontSize: "inherit",
      }}
    >
      {label}
    </button>
  );

  // Build Ant Design Breadcrumb items based on activeTab
  const getBreadcrumbItems = () => {
    if (isSuperAdmin) {
      const rootItem = {
        title: navBtn("requests", "Super Admin", "#7c3aed", 600),
      };
      return [rootItem, { title: <span style={{ color: "#0f172a", fontWeight: 500 }}>{title}</span> }];
    }

    const homeItem = {
      title: navBtn("overview", isEmployee ? "My Workspace" : "Dashboard", "#64748b"),
    };

    if (activeTab === "overview") {
      return [homeItem];
    }

    return [homeItem, { title: <span style={{ color: "#0f172a", fontWeight: 500 }}>{title}</span> }];
  };

  // ── 0. Super Admin Role Dropdown Items ──
  const superAdminMenuItems = [
    {
      key: "superadmin_header",
      type: "group",
      label: (
        <div style={{ padding: "4px 0 6px 0", cursor: "default" }}>
          <Text strong style={{ fontSize: "0.9rem", color: "#0f172a", display: "block" }}>
            Super Admin
          </Text>
          <Flex align="center" gap={6} style={{ marginTop: 2 }}>
            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
              {user?.email || "admin@softrate.com"}
            </Text>
            <Tag color="purple" style={{ margin: 0, fontSize: "0.65rem", padding: "0 4px", lineHeight: "16px" }}>
              Root Access
            </Tag>
          </Flex>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "change_photo",
      icon: <Camera01Icon size={16} color="#2563eb" />,
      label: "Change Profile Photo",
      onClick: () => setIsPhotoModalOpen(true),
    },
    {
      key: "requests",
      icon: <Clock01Icon size={16} color="#64748b" />,
      label: "Requests Dashboard",
      onClick: () => onNavigateTab?.("requests"),
    },
    {
      key: "companies",
      icon: <UserGroupIcon size={16} color="#64748b" />,
      label: "Company Management",
      onClick: () => onNavigateTab?.("companies"),
    },
    {
      key: "rm",
      icon: <UserIcon size={16} color="#64748b" />,
      label: "Assign Relationship Manager",
      onClick: () => onNavigateTab?.("rm"),
    },
    { type: "divider" },
    {
      key: "superadmin_logout",
      icon: <Logout01Icon size={16} color="#e11d48" />,
      label: <span style={{ color: "#e11d48", fontWeight: 600 }}>Sign Out</span>,
      onClick: () => setIsLogoutModalOpen(true),
    },
  ];

  // ── 1. Admin Role Dropdown Items ──
  const adminMenuItems = [
    {
      key: "admin_header",
      type: "group",
      label: (
        <div style={{ padding: "4px 0 6px 0", cursor: "default" }}>
          <Text strong style={{ fontSize: "0.9rem", color: "#0f172a", display: "block" }}>
            {companyName}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem", display: "block", marginTop: 2 }}>
            {companyCode}
          </Text>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "change_photo",
      icon: <Camera01Icon size={16} color="#2563eb" />,
      label: "Change Profile Photo",
      onClick: () => setIsPhotoModalOpen(true),
    },
    {
      key: "company",
      icon: <BankIcon size={16} color="#64748b" />,
      label: "Company Settings",
      onClick: () => onNavigateTab?.("company"),
    },
    {
      key: "settings",
      icon: <Settings02Icon size={16} color="#64748b" />,
      label: "App Settings",
      onClick: () => onNavigateTab?.("settings"),
    },
    {
      key: "invoice",
      icon: <File01Icon size={16} color="#64748b" />,
      label: "Invoice & Proposal",
      onClick: () => onNavigateTab?.("invoice"),
    },
    {
      key: "employees",
      icon: <UserGroupIcon size={16} color="#64748b" />,
      label: "Team Management",
      onClick: () => onNavigateTab?.("employees"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <Logout01Icon size={16} color="#e11d48" />,
      label: <span style={{ color: "#e11d48", fontWeight: 600 }}>Sign Out</span>,
      onClick: () => setIsLogoutModalOpen(true),
    },
  ];

  // ── 2. Employee Role Dropdown Items ──
  const employeeMenuItems = [
    {
      key: "emp_header",
      type: "group",
      label: (
        <div style={{ padding: "4px 0 6px 0", cursor: "default" }}>
          <Text strong style={{ fontSize: "0.9rem", color: "#0f172a", display: "block" }}>
            {user?.name || "Employee"}
          </Text>
          <Flex align="center" gap={6} style={{ marginTop: 2 }}>
            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
              {user?.mobile || user?.email || companyCode}
            </Text>
            <Tag color="blue" style={{ margin: 0, fontSize: "0.65rem", padding: "0 4px", lineHeight: "16px" }}>
              Sales Rep
            </Tag>
          </Flex>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "change_photo",
      icon: <Camera01Icon size={16} color="#2563eb" />,
      label: "Change Profile Photo",
      onClick: () => setIsPhotoModalOpen(true),
    },
    {
      key: "leads",
      icon: <UserIcon size={16} color="#64748b" />,
      label: "My Leads",
      onClick: () => onNavigateTab?.("leads"),
    },
    {
      key: "today-calls",
      icon: <Calendar03Icon size={16} color="#64748b" />,
      label: "Today's Follow-ups",
      onClick: () => onNavigateTab?.("today-calls"),
    },
    { type: "divider" },
    {
      key: "emp_logout",
      icon: <Logout01Icon size={16} color="#e11d48" />,
      label: <span style={{ color: "#e11d48", fontWeight: 600 }}>Sign Out</span>,
      onClick: () => setIsLogoutModalOpen(true),
    },
  ];

  let menuItems = adminMenuItems;
  if (isSuperAdmin) {
    menuItems = superAdminMenuItems;
  } else if (isEmployee) {
    menuItems = employeeMenuItems;
  }

  const effectivePhoto = user?.profilePhoto || companyInfo?.profilePhoto || "";

  return (
    <>
      <header className="topbar-navbar">
        {/* ── COMMON: Left End Breadcrumbs & Section Title ── */}
        <div className="topbar-left-section">
          <Flex vertical justify="center" gap={1}>
            <Breadcrumb items={getBreadcrumbItems()} className="topbar-breadcrumb" separator="/" />
            <Title level={4} className="topbar-title">
              {title}
            </Title>
          </Flex>
        </div>

        {/* ── COMMON: Center Global Search Bar with Multi-Entity Suggestions ── */}
        <div className="topbar-center-section">
          <div className="global-search-container">
            <Input
              ref={searchManager.inputRef}
              className="topbar-global-search"
              prefix={<Search01Icon size={16} color="#94a3b8" style={{ marginRight: 8 }} />}
              suffix={
                <span className="gs-kbd" style={{ cursor: "default", userSelect: "none" }}>
                  {SEARCH_SHORTCUT_LABEL}
                </span>
              }
              placeholder="Search leads, pipeline, invoices, proposals..."
              value={searchManager.query}
              onChange={(e) => {
                searchManager.handleQueryChange(e.target.value);
                onSearch?.(e.target.value);
              }}
              onFocus={searchManager.handleFocus}
              onBlur={searchManager.handleBlur}
              onKeyDown={searchManager.handleKeyDown}
              allowClear
            />

            <GlobalSearchDropdown
              isOpen={searchManager.isOpen}
              loading={searchManager.loading}
              query={searchManager.query}
              suggestions={searchManager.suggestions}
              selectedIndex={searchManager.selectedIndex}
              recentSearches={searchManager.recentSearches}
              onSelectSuggestion={searchManager.handleSelectSuggestion}
              onSelectRecentSearch={searchManager.handleSelectRecentSearch}
              onRemoveRecentSearch={searchManager.removeRecentSearch}
              onClearAllRecentSearches={searchManager.clearAllRecentSearches}
            />
          </div>
        </div>

        {/* ── ROLE-DRIVEN: Right Section Actions & User Pill ── */}
        <div className="topbar-right-section">
          <Flex align="center" gap={12}>
            {/* Employee Break Toggle */}
            {isEmployee && (
              <Tooltip
                title={
                  autoBreak.isOnBreak
                    ? `On break (Paused for ${formatBreakTime(autoBreak.breakElapsedSeconds)} | Today: ${Math.round((autoBreak.todayTotalBreakSeconds + autoBreak.breakElapsedSeconds) / 60)} / ${autoBreak.breakHourLimit} mins). Click to resume.`
                    : `Take a break (Today's total: ${Math.round(autoBreak.todayTotalBreakSeconds / 60)} / ${autoBreak.breakHourLimit} mins)`
                }
              >
                <Button
                  type="default"
                  size="small"
                  icon={<Coffee01Icon size={15} color={autoBreak.isOnBreak ? "#f59e0b" : "#64748b"} style={{ verticalAlign: "middle" }} />}
                  className={`topbar-metric-btn ${autoBreak.isOnBreak ? "break-active" : ""}`}
                  onClick={() => (autoBreak.isOnBreak ? autoBreak.endBreak() : autoBreak.startBreak(true))}
                >
                  {autoBreak.isOnBreak
                    ? `On Break (${formatBreakTime(autoBreak.breakElapsedSeconds)})`
                    : "Break"}
                </Button>
              </Tooltip>
            )}

            {/* Deal Pipeline Stagnation & Activity Notifications */}
            {!isSuperAdmin && (
              <TopNavbarNotifications
                companyCode={companyCode}
                employeeId={user?.id || user?._id || user?.employeeId}
                isEmployee={isEmployee}
                onNavigateTab={onNavigateTab}
              />
            )}

            {/* Profile Pill & Role-Based Menu */}
            <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight" arrow={{ pointAtCenter: true }}>
              <button type="button" className="topbar-profile-pill">
                <div className="topbar-avatar-wrapper">
                  <Avatar className="topbar-avatar" src={effectivePhoto || null}>
                    {getInitials(displayName)}
                  </Avatar>
                  <span className="topbar-status-dot" />
                </div>
                <span className="topbar-user-label" title={displayName}>
                  {displayName}
                </span>
                <ArrowDown01Icon size={12} className="topbar-chevron-icon" />
              </button>
            </Dropdown>
          </Flex>
        </div>
      </header>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal open={isLogoutModalOpen} onCancel={() => setIsLogoutModalOpen(false)} onConfirm={handleConfirmLogout} userName={displayName} companyName={companyName} />

      {/* Profile Photo Upload Modal */}
      <ProfilePhotoModal
        open={isPhotoModalOpen}
        onCancel={() => setIsPhotoModalOpen(false)}
        currentPhoto={effectivePhoto}
        userName={displayName}
        companyName={companyName}
        role={role}
        companyCode={companyCode}
        employeeId={user?.id || user?._id || user?.employeeId}
        userInfo={user}
      />
    </>
  );
}

export default TopNavbar;

